import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight } from '@fortawesome/free-solid-svg-icons';

import './styles.scss';

interface PaginationProps {
  total: number;
  pageSize: number;
  page: number;
  onPageChange: (page: number) => void;
  loading?: boolean;
  maxPagesToShow?: number;
}

const Pagination: React.FC<PaginationProps> = ({
  total,
  pageSize,
  page,
  onPageChange,
  loading = false,
  maxPagesToShow = 7,
}) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const makePageList = () => {
    const pages: Array<number | string> = [];
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    if (page <= 4) {
      for (let i = 1; i <= 5; i++) pages.push(i);
      pages.push('...');
      pages.push(totalPages);
      return pages;
    }

    if (page >= totalPages - 3) {
      pages.push(1);
      pages.push('...');
      for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    pages.push(1);
    pages.push('...');
    pages.push(page - 1);
    pages.push(page);
    pages.push(page + 1);
    pages.push('...');
    pages.push(totalPages);
    return pages;
  };

  const pages = makePageList();

  return (
    <div className="pagination-container">
      <div className="pagination-controls">
        <button
          className="btn page-btn icon-btn"
          disabled={loading || page <= 1}
          onClick={() => onPageChange(Math.max(1, page - 1))}
          aria-label="prev"
        >
          <FontAwesomeIcon icon={faAngleLeft} />
        </button>

        {pages.map((p, idx) =>
          typeof p === 'string' ? (
            <span key={`ellipsis-${idx}`} className="pagination-ellipsis">
              {p}
            </span>
          ) : (
            <button
              key={`p-${p}`}
              className={`btn page-number ${p === page ? 'active' : ''}`}
              onClick={() => onPageChange(Number(p))}
              aria-current={p === page}
              disabled={loading}
              aria-disabled={loading}
            >
              {p}
            </button>
          ),
        )}

        <button
          className="btn page-btn icon-btn"
          disabled={loading || page >= totalPages}
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          aria-label="next"
        >
          <FontAwesomeIcon icon={faAngleRight} />
        </button>
      </div>
    </div>
  );
};

export default Pagination;
